import { useCallback, useEffect, useMemo } from 'react';
import { useContext } from 'react';
import { BulkActionItem } from 'src/components/Listings/InventoryActionDropdown/components/types';
import { useCommandMenuContext } from 'src/contexts/CommandMenuContext/CommandMenuContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { useEventCallback } from 'src/hooks/useEventCallback';
import { useUserCanFulfillSale } from 'src/hooks/useUserHasSalePermissions';
import { BulkUploadSaleBarcodesModal } from 'src/modals/UploadBarcodes';
import { ContentId } from 'src/utils/constants/contentId';
import { CommandOptionType, Event, SaleQuery } from 'src/WebApiController';

import { LaunchBulkUpdateBarcodes } from './LaunchBulkUpdateBarcodes';

export const useBulkUpdateBarcodes = (
  filterQueryWithEventIds: SaleQuery,
  setIsLoading: (v: boolean) => void,
  isLoading?: boolean,
  event?: Event
) => {
  const { setModal } = useContext(ModalContext);
  const { registerCommand, unregisterCommand } = useCommandMenuContext();

  const hasFulfillPermission = useUserCanFulfillSale(
    undefined /* sale */,
    true /* forBulkEdit */
  );

  const launchModal = useCallback(() => {
    setModal({
      children: (
        <BulkUploadSaleBarcodesModal
          event={event}
          filterQueryWithEventIds={filterQueryWithEventIds}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  }, [event, filterQueryWithEventIds, setModal]);

  const openDialogStableRef = useEventCallback(() => {
    setModal({
      children: (
        <BulkUploadSaleBarcodesModal
          event={event}
          filterQueryWithEventIds={filterQueryWithEventIds}
        />
      ),
      clickOutsideToClose: true,
      size: 'slide-in',
    });
  });

  useEffect(() => {
    registerCommand({
      command: {
        id: CommandOptionType.SalesBulkUpdateBarcodes,
        onSelect: openDialogStableRef,
      },
      disabled: !hasFulfillPermission,
    });

    return () => {
      unregisterCommand({
        commandId: CommandOptionType.SalesBulkUpdateBarcodes,
        disabled: !hasFulfillPermission,
      });
    };
  }, [
    hasFulfillPermission,
    openDialogStableRef,
    registerCommand,
    unregisterCommand,
  ]);

  const actionItems = useMemo<BulkActionItem[]>(() => {
    if (!hasFulfillPermission) {
      return [];
    }

    return [
      {
        title: ContentId.UploadBarcodes,
        onClick: (e) => {
          e.preventDefault();
          e.stopPropagation();
          launchModal();
        },
        disabled: isLoading,
      },
    ];
  }, [hasFulfillPermission, isLoading, launchModal]);

  return {
    dropDown: (
      <LaunchBulkUpdateBarcodes
        key="bulkUpdateBarcodes"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          launchModal();
        }}
        disabled={isLoading}
      />
    ),
    actionItems,
  };
};
